// Taking a photo in the browser (0.10.2): on iOS and Android the native
// camera app (`<input capture="environment">`) gives the better photo; on
// every other device with a camera (Windows tablets such as the Surface,
// laptops with a webcam) the CameraCapture dialog uses getUserMedia, because
// there the capture attribute is ignored and a keyboard makes the pointer
// "fine". The choices are pure and tested (scripts/camera.test.mjs).
import { useEffect, useState } from "react";
/** iOS (iPadOS reports a Mac with touch) or Android: the native camera app is used. */
export function isMobileOS(userAgent, platform = "", maxTouchPoints = 0) {
    const ua = userAgent || "";
    if (/android/i.test(ua))
        return true;
    if (/iphone|ipad|ipod/i.test(ua))
        return true;
    // iPadOS 13+ presents itself as a Mac; only an iPad has touch there
    return /mac/i.test(platform || ua) && maxTouchPoints > 1 && /safari/i.test(ua) && !/chrome|edg/i.test(ua);
}
/** The video inputs of a device list. */
export function videoInputs(devices) {
    return devices.filter((d) => d.kind === "videoinput");
}
const REAR = /\b(rear|back|environment|world)\b|rück|rueck|arrière|arriere/i;
const FRONT = /\b(front|user|facing front|integrated|webcam)\b|vorder|avant/i;
/**
 * The rear camera of a device list, by its label (labels are known after the
 * permission; a Surface names it "Microsoft Camera Rear"): null when no label
 * says rear or back (then facingMode "environment" stays the choice).
 */
export function pickRearCamera(devices) {
    const cams = videoInputs(devices);
    const rear = cams.find((d) => REAR.test(d.label) && !/\bfront\b/i.test(d.label));
    return rear ? rear.deviceId : null;
}
/** The next camera of the switch button (cycles through the video inputs). */
export function nextCamera(devices, currentId) {
    const cams = videoInputs(devices).filter((d) => d.deviceId);
    if (cams.length < 2)
        return null;
    const i = cams.findIndex((d) => d.deviceId === currentId);
    return cams[(i + 1) % cams.length]?.deviceId ?? null;
}
/** A camera's label reads as the front camera (for a short hint, never for a choice). */
export const isFrontLabel = (label) => FRONT.test(label) && !REAR.test(label);
/** What a getUserMedia failure means for the user. */
export function cameraErrorKind(err) {
    const name = err && typeof err === "object" && "name" in err ? String(err.name) : "";
    switch (name) {
        case "NotAllowedError":
        case "PermissionDeniedError":
        case "SecurityError":
            return "denied";
        case "NotFoundError":
        case "DevicesNotFoundError":
        case "OverconstrainedError":
            return "none";
        case "NotReadableError":
        case "TrackStartError":
        case "AbortError":
            return "busy";
        default:
            return "other";
    }
}
/** The constraints of a start: the rear camera by id, else facingMode "environment"; 1920 by 1080 ideally. */
export function cameraConstraints(deviceId) {
    const size = { width: { ideal: 1920 }, height: { ideal: 1080 } };
    return {
        audio: false,
        video: deviceId ? { deviceId: { exact: deviceId }, ...size } : { facingMode: { ideal: "environment" }, ...size },
    };
}
/**
 * native on iOS and Android, stream where getUserMedia and at least one video
 * input exist, none otherwise (then only the file picker shows). "none" while
 * the devices are being counted.
 */
export function useCameraMode() {
    const [mode, setMode] = useState(() => {
        if (typeof navigator === "undefined")
            return "none";
        return isMobileOS(navigator.userAgent, navigator.platform, navigator.maxTouchPoints) ? "native" : "none";
    });
    useEffect(() => {
        if (mode === "native" || typeof navigator === "undefined")
            return;
        const md = navigator.mediaDevices;
        if (!md?.getUserMedia || !md.enumerateDevices)
            return;
        let alive = true;
        const count = () => md.enumerateDevices().then((list) => alive && setMode(videoInputs(list).length > 0 ? "stream" : "none"), () => undefined);
        void count();
        md.addEventListener?.("devicechange", count);
        return () => {
            alive = false;
            md.removeEventListener?.("devicechange", count);
        };
    }, [mode]);
    return mode;
}
